import {
  Disclosure as Collapsible,
  DisclosureButton as CollapsibleButton,
  DisclosurePanel as CollapsiblePanel,
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import clsx from 'clsx'

import { create } from 'zustand'
import Button from '../Button/Button'
import { HelpCenterButton } from '../HelpCenter/HelpCenter'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import Logo, { Brand } from '../Logo/Logo'
import {
  PrimaryHeader,
  PrimaryHeaderOption,
  SiteHeaderProps,
} from './SiteHeader'

interface MobileMenuModalProps
  extends Omit<PrimaryHeader, 'type'>,
    Pick<SiteHeaderProps, 'brand'> {}

interface CollapsibleMenuProps {
  /**
   * Text of the callapsible button
   */
  name: string
  /**
   * List of items for content
   * eg. [{ name: 'lorem ipsum', href: 'sample.com' }]
   */
  options: PrimaryHeaderOption[]
}

function CollapsibleMenu({ name, options }: CollapsibleMenuProps) {
  return (
    <Collapsible>
      {({ open }) => (
        <>
          <CollapsibleButton className="flex items-center gap-2 type-body-4">
            <span>{name}</span>
            <Icon
              icon={open ? 'chevron-up' : 'chevron-down'}
              className="w-6 h-6"
            />
          </CollapsibleButton>
          <CollapsiblePanel className="mt-4">
            <ul className="flex flex-col gap-3 [&_a]:px-2">
              {options.map(({ name, href, onClick }, key) => (
                <li key={key}>
                  <Link
                    inheritFont
                    href={href}
                    onClick={onClick}
                    className="type-body-4 !no-underline !h-auto"
                  >
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </CollapsiblePanel>
        </>
      )}
    </Collapsible>
  )
}

export interface ModalStore {
  dialogOpen: boolean
  openDialog: () => void
  hideDialog: () => void
}

export const useModal = create<ModalStore>((set) => ({
  dialogOpen: false,
  openDialog: () => set({ dialogOpen: true }),
  hideDialog: () => set({ dialogOpen: false }),
}))

export default function MobileMenuModal({
  brand,
  brands,
  links,
  button,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  helpLinks,
  userLinks,
}: MobileMenuModalProps) {
  const [isOpen, hide] = useModal((state) => [
    state.dialogOpen,
    state.hideDialog,
  ])
  const logoBrand = (brand + 'dark') as Brand

  return (
    <Transition
      show={isOpen}
      enter="ease-out duration-100"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200 delay-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Dialog
        as="div"
        className="relative z-20 focus:outline-none"
        onClose={hide}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div
            className="fixed inset-0 bg-theme-primary/50"
            aria-hidden="true"
          />
          <div className="relative h-full w-full pr-[72px]">
            <TransitionChild
              enter="ease-out duration-300 delay-100"
              enterFrom="opacity-0 translate-x-[-100%]"
              enterTo="opacity-100 translate-x-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-x-0"
              leaveTo="opacity-0 translate-x-[-100%]"
            >
              <DialogPanel
                className={clsx(
                  'relative w-full h-full p-6 bg-white flex flex-col gap-6',
                )}
              >
                <div className="flex flex-col justify-between gap-6 h-full">
                  <div className="flex flex-col gap-6 overflow-auto">
                    <div className="flex justify-end pt-2">
                      <button className="w-8 h-8" onClick={hide}>
                        <Icon icon="close" className="w-full h-full" />
                      </button>
                    </div>
                    <div>
                      <Logo brand={logoBrand} className="w-auto h-[40px]" />
                    </div>
                    <div>
                      <ul className={clsx('flex flex-col gap-6')}>
                        {brands && brands.length > 0 && (
                          <li>
                            <Collapsible>
                              {({ open }) => (
                                <>
                                  <CollapsibleButton className="flex items-center gap-2 type-body-4">
                                    <span>Our Brands</span>
                                    <Icon
                                      icon={
                                        open ? 'chevron-up' : 'chevron-down'
                                      }
                                      className="w-6 h-6"
                                    />
                                  </CollapsibleButton>
                                  <CollapsiblePanel className="mt-4">
                                    <ul className="flex flex-col gap-3 [&_a]:px-2">
                                      {brands.map(
                                        ({ brand: brandName, url }, key) => {
                                          const logoVariant = (brandName +
                                            'textonlydark') as Brand
                                          return (
                                            <li key={key}>
                                              <Link
                                                className={clsx(
                                                  brandName === brand &&
                                                    'opacity-100',
                                                  brandName !== brand &&
                                                    'opacity-60',
                                                  'h-[16px] flex items-center !no-underline',
                                                )}
                                                href={url}
                                                onClick={hide}
                                              >
                                                <Logo
                                                  brand={logoVariant}
                                                  width="auto"
                                                  height="16"
                                                />
                                              </Link>
                                            </li>
                                          )
                                        },
                                      )}
                                    </ul>
                                  </CollapsiblePanel>
                                </>
                              )}
                            </Collapsible>
                          </li>
                        )}
                        {links.length > 0 &&
                          links.map(({ name, href }, key) => (
                            <li key={key}>
                              <Link
                                href={href}
                                inheritFont
                                className="type-body-4 !px-0 no-underline h-[unset]"
                              >
                                {name}
                              </Link>
                            </li>
                          ))}
                        <li>
                          <HelpCenterButton
                            render={(buttonProps) => (
                              <button
                                className="type-body-4 flex items-center gap-2"
                                {...buttonProps}
                                onClick={(e) => {
                                  hide()
                                  buttonProps.onClick(e)
                                }}
                              >
                                Help
                              </button>
                            )}
                          />
                        </li>
                        {userLinks.length > 0 && (
                          <li>
                            <CollapsibleMenu
                              name="Account"
                              options={userLinks}
                            />
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  {button &&
                    (typeof button === 'object' &&
                    ('href' in button || 'onClick' in button) ? (
                      <Button
                        link={
                          (button as { href?: string }).href
                            ? { href: (button as { href: string }).href }
                            : undefined
                        }
                        onClick={(button as { onClick?: () => void }).onClick}
                        className="type-body-9 w-full !min-h-12"
                      >
                        {(button as { name: string }).name}
                      </Button>
                    ) : (
                      (button as React.ReactNode)
                    ))}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
